import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChatPanel } from './ChatPanel';
import { useChat } from '../../state/useChat';
import { useAttachment } from '../../state/useAttachment';
import { dragHasFile, imageFrom } from '../../lib/image';
import {
  CHAT_DEFAULT,
  CHAT_MAX,
  CHAT_MIN,
  DOCK_EDGE,
  chatPrefs,
  clampDock,
  clampSize,
  updatePrefs,
  type Dock,
  type Size,
} from '../../lib/chat';
import { ease } from '../../lib/motion';
import { setCorner, setCornerSignal } from '../../lib/corner';
import { onOpenRoom, onOpenSupport, takeSupportAsk } from '../../lib/dock';
import type { RoomId } from '@engineer-os/chat';
import { objectiveSnapshot, subscribeObjective } from '../../lib/presence';
import { useCorner } from '../../state/useCorner';
import { rankFor } from '../../progress';
import { useLocale, useStrings } from '../../state/useLocale';
import type { ChatDockProps } from '../contracts';

/**
 * The corner bubble and the panel it opens.
 *
 * Chat is a side conversation to the level, so it lives at the edge and is
 * shut by default — but it is not hidden. The way in is a round bubble in the
 * corner with a green ring around it, at full strength whether or not anything
 * is happening, and it is the one interface element in this product allowed to
 * be that loud. It spent its first life as a dim 30px square beside the mute
 * toggle, on the reasoning that chat is a side conversation; the flaw in that
 * reasoning is that a room nobody can find is a room nobody is in, and this is
 * the one part of the product that is worth more the more people are in it.
 * When it has something to say it says it twice: a count in a small white
 * mono badge, never a hue, and a ring that leaves the bubble once every two
 * seconds. The count is the room's unread plus the mentions the account is
 * holding, and the badge carries a mark when any of it is the second kind —
 * a line that named you is worth opening the panel for in a way that a busy
 * room is not. The panel opens above it as a card that leaves the work pane
 * visible, rather than a full-height drawer down the side of the screen. It is
 * the same bubble on the boot screen, on the level list and inside a level,
 * mounted once for the whole visit (App.tsx), so the conversation is never
 * something the learner has to go back for.
 *
 * Four things are remembered per browser, and all four are preferences rather
 * than state: whether the panel was open, where it was dragged to, how big it
 * was pulled, and the room. A learner who works with it open gets it back on
 * the next visit, in the corner they put it and the size they made it.
 *
 * The socket outlives the panel, and now the first click too: the hook
 * connects on load and listens, so closing this hides the conversation without
 * leaving the room, and opening it for the first time joins one that has been
 * running all along.
 */
/** How much one arrow key moves an edge, in pixels. The workstation's own. */
const RESIZE_STEP = 16;

export function ChatDock({ progress, mission, notify, account }: ChatDockProps) {
  const [open, setOpen] = useState(() => chatPrefs().open);
  // The composer's draft lives here rather than in the composer so that
  // closing the panel with Escape does not throw away a half-written line.
  const [draft, setDraft] = useState('');
  // And the picture that has been picked and not yet sent, for the same
  // reason and one more: it holds an object URL, which has to be released
  // exactly once (state/useAttachment.ts).
  const attach = useAttachment();
  /**
   * True while a drag carrying a file is over the panel.
   *
   * A counter rather than a boolean, and the reason is `dragleave`: it fires
   * every time the pointer crosses into a child element, so a naive
   * enter/leave pair flickers the highlight off as the drag passes over the
   * message list, the composer, a button. Counting enters and leaves means the
   * highlight goes off exactly once, when the last one is unbalanced.
   */
  const [over, setOver] = useState(0);
  const [dock, setDock] = useState<Dock | null>(() => chatPrefs().dock);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  // Read by handlers that run between renders — a drag, a resize — where the
  // state variable they closed over may be a frame behind.
  const dockRef = useRef(dock);
  dockRef.current = dock;

  /*
   * The drag arithmetic needs to know which way round the page is.
   *
   * `clientX` is physical — it grows to the right whatever the document's
   * direction — while the dock is stored as a distance from the inline end.
   * In Arabic those point opposite ways, so every pointer delta on the inline
   * axis flips sign. Everything else about the panel is direction-agnostic:
   * a width is a width, and `bottom` is on the block axis.
   */
  const rtl = useLocale().dir === 'rtl';

  // Through the hook rather than `rankFor(...).name` alone, so the rank in the
  // member list repaints when the language changes.
  const s = useStrings();
  const rank = rankFor(progress.xp).current.name;
  const level = mission ? mission.level : null;
  /*
   * The whole progress record goes to the hook, not just the rank.
   *
   * A cleared level is announced in the room over the socket that is already
   * open, and the hook is what holds that socket. The alternative — the game
   * calling the Worker over HTTP when a level is cleared — would have put a
   * second path from the tab to the chat service for a fact the first path is
   * already carrying, and docs/architecture.md draws that line deliberately:
   * the chat is one connection, and everything the chat knows arrives on it.
   */
  /*
   * Which objective the learner is on, straight from the level screen.
   *
   * It arrives through a module store rather than as a prop because the only
   * component that knows it is `Play`, which is this component's sibling
   * rather than its parent, and the shared parent is `App` — which re-renders
   * the terminal and the editor with it. Paying that on every grade so a
   * member list can say "on objective 2" is the wrong trade, and
   * `useSyncExternalStore` subscribes exactly this component instead
   * (lib/presence.ts makes the whole argument).
   *
   * The same function is passed for the server snapshot: there is no server
   * here, and a level nobody is playing is null in both worlds anyway.
   */
  const objective = useSyncExternalStore(subscribeObjective, objectiveSnapshot, objectiveSnapshot);
  const chat = useChat({ open, rank, level, objective, progress, notify, account: account.user });

  const show = useCallback((next: boolean) => {
    setOpen(next);
    updatePrefs({ open: next });
    // A panel whose bubble is folded away is a door with no handle: the
    // header's close button would still work, but the thing the learner
    // reaches for — the bubble they opened it from — would not be on screen.
    // So opening brings the corner back, whether it was opened by a click on
    // the bubble or by Ctrl+/ with the cluster already folded.
    if (next) setCorner('shown');
  }, []);

  /**
   * Somebody outside the dock asked for a room (lib/dock.ts).
   *
   * The profile screen's team list is the only caller today: a team's room is
   * where its roster can actually talk, so a list of teams that cannot open one
   * is a list of names.
   *
   * The handler goes through a ref rather than into the dependency array
   * because `chat` is a new object on every render of this component — which
   * is every message that arrives — and an effect that depended on it would
   * unsubscribe and resubscribe on each one. The subscription is for the life
   * of the dock, which is the life of the visit.
   */
  const ask = useRef<(room: RoomId) => void>(() => {});
  ask.current = (room: RoomId) => {
    show(true);
    // Pinned, exactly as picking a room by hand is: walking into a level
    // afterwards should not evict somebody from the team room they just asked
    // for (components/chat/ChatPanel.tsx, the teams popover).
    chat.join(room);
  };
  useEffect(() => onOpenRoom((room) => ask.current(room)), []);

  /**
   * And the other ask: the support desk, with no room named (lib/dock.ts).
   *
   * A counter rather than a boolean, because it is a request and not a state:
   * somebody who opens the desk, reads it, closes the popover and presses the
   * offer block's line again means it the second time too, and a boolean that
   * was already true would do nothing.
   *
   * Claimed rather than merely listened for, and the effect claims once on
   * mount before subscribing. That is what makes this work on the landing
   * page, where the press is what mounts the dock in the first place and the
   * listener therefore does not exist when the ask is made.
   */
  const [supportAsk, setSupportAsk] = useState(0);
  useEffect(() => {
    const claim = () => {
      if (!takeSupportAsk()) return;
      show(true);
      setSupportAsk((n) => n + 1);
    };
    claim();
    return onOpenSupport(claim);
  }, [show]);

  const close = useCallback(() => {
    show(false);
    // Focus was inside the panel, which is about to be removed; the toggle
    // is where a keyboard user expects to be standing afterwards.
    toggleRef.current?.focus();
  }, [show]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    // A popover inside the panel handles its own Escape and stops it here.
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    e.preventDefault();
    close();
  };

  /* ── The shortcut ───────────────────────────────────────────────────── */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ctrl+/ — Cmd+/ as well, since a Mac keyboard reaches for that first,
      // and both are accepted rather than detected. It is unclaimed in every
      // browser this runs in, and it is not a key sequence the terminal or the
      // editor below has any use for. Shift is not excluded: "/" is a shifted
      // key on several layouts.
      if (e.key !== '/' || !(e.metaKey || e.ctrlKey) || e.altKey || e.defaultPrevented) return;
      e.preventDefault();
      // Opening leaves focus alone: the composer inside takes it on mount, the
      // way it does for a click. Closing puts it back on the toggle.
      if (open) close();
      else show(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, show]);

  /* ── The fold ───────────────────────────────────────────────────────── */

  /**
   * The panel is never open while the corner is folded.
   *
   * `show` keeps one half of that — opening unfolds — and this keeps the
   * other. Somebody who folds the corner is asking for the screen back, and
   * leaving a 320px card floating over the work with its bubble gone would
   * be answering a different question. Nothing is lost by it: the dock stays
   * mounted, so the draft in the composer, the room and the socket all
   * survive, exactly as they do for Escape.
   *
   * `show(false)` rather than `close()`, because focus belongs on the fold
   * the learner just pressed and not on a bubble that is on its way off the
   * screen.
   */
  const { folded } = useCorner();

  useEffect(() => {
    if (folded && open) show(false);
  }, [folded, open, show]);

  /* ── Where it sits ──────────────────────────────────────────────────── */

  /**
   * Dragging the panel by its header.
   *
   * Pointer events with capture, so one set of handlers covers mouse, pen and
   * touch, and a fast drag that leaves the header — or the window — keeps its
   * grip until the button comes up. The position is kept as a distance from
   * the bottom **inline-end** corner rather than a point, which is what makes
   * a resized window carry the panel with the corner it was put near instead
   * of leaving it half outside; clampDock (lib/chat) handles the rest.
   *
   * Inline-end rather than right because that corner is on the left in
   * Arabic, and the stored number has to mean the same thing in both
   * languages — "how far from the bubble" — or a language switch would throw
   * the panel across the window. The pointer arithmetic is where that costs
   * something: `clientX` is physical and always grows to the right, so in a
   * right-to-left page the sign has to flip.
   */
  const drag = useRef<{ id: number; x: number; y: number; from: Dock } | null>(null);

  const fit = useCallback((next: Dock): Dock => {
    const panel = panelRef.current;
    if (!panel) return next;
    return clampDock(next, { w: panel.offsetWidth, h: panel.offsetHeight }, { w: window.innerWidth, h: window.innerHeight });
  }, []);

  const grip = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      // The header's own background only. Anything inside it — the room
      // picker, the member count, the close button — is a control, and a
      // control that moved the window under the finger would be unusable.
      if (e.target !== e.currentTarget || e.button !== 0) return;
      const panel = panelRef.current;
      if (!panel) return;
      const box = panel.getBoundingClientRect();
      drag.current = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        // Measured, not read from state: the panel may be sitting at the
        // stylesheet's default, which is no Dock at all.
        // Measured from the inline end: in Arabic that is the window's left
        // edge, so the distance is the box's own left offset.
        from: {
          end: rtl ? box.left : window.innerWidth - box.right,
          bottom: window.innerHeight - box.bottom,
        },
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      // Bottom always grows the opposite way to y: dragging up moves the
      // panel away from the corner it is measured from. The inline axis does
      // the same in a left-to-right page, and the opposite in a right-to-left
      // one, where moving right is moving away from the corner.
      const dx = e.clientX - d.x;
      setDock(fit({ end: d.from.end + (rtl ? dx : -dx), bottom: d.from.bottom - (e.clientY - d.y) }));
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      drag.current = null;
      e.currentTarget.releasePointerCapture(e.pointerId);
      // Written once, at the end. A drag is one decision, not sixty writes.
      if (dockRef.current) updatePrefs({ dock: dockRef.current });
    },
    onPointerCancel: () => {
      drag.current = null;
    },
  };

  /* ── How big it is ──────────────────────────────────────────────────── */

  /**
   * Three handles: the left edge, the top edge, and the corner between them.
   *
   * Only those three, and the reason is the Dock. The panel's position is
   * stored as a distance from the bottom-right corner of the window, so that
   * corner is the one thing about the panel that does not move — which makes
   * the other two edges the only ones that can be pulled without the panel
   * walking across the screen as it grows. Handles on all four sides would
   * mean resizing the bottom edge had to move the panel at the same time to
   * keep the top still, and a panel that slides while you resize it is a panel
   * you cannot aim. So: away from the anchored corner is bigger, towards it is
   * smaller, and the corner you are pulling against stays under the bubble it
   * came out of.
   *
   * The same separator the workstation uses (PlayFrame), down to the details
   * that matter: the pointer gesture writes the custom property straight to
   * the DOM so a drag does not re-render the conversation sixty times a
   * second, the handles are focusable and move with the arrow keys, Home puts
   * the size back, and the preference is written once, at the end.
   *
   * The stylesheet keeps the last word. `.chat` reads these two properties
   * through a `min()` against the window, so a panel that no longer fits is
   * drawn smaller without anything being written: shrink the window and the
   * panel shrinks, open it back up and the size the learner chose is still
   * theirs. That is why nothing here clamps on a window resize the way the
   * dock has to.
   */
  const [size, setSize] = useState<Size | null>(() => chatPrefs().size);

  /** What the panel has to grow into, measured from where it actually is. */
  const room = useCallback((): Size => {
    const panel = panelRef.current;
    if (!panel) return CHAT_MAX;
    const box = panel.getBoundingClientRect();
    return { w: box.right - DOCK_EDGE, h: box.bottom - DOCK_EDGE };
  }, []);

  /** How big it is on screen right now, which is not always what was stored. */
  const measure = useCallback((): Size => {
    const panel = panelRef.current;
    if (!panel) return CHAT_DEFAULT;
    const box = panel.getBoundingClientRect();
    return { w: box.width, h: box.height };
  }, []);

  const resizeTo = useCallback(
    (next: Size) => {
      const fitted = clampSize(next, room());
      setSize(fitted);
      updatePrefs({ size: fitted });
      // Whatever measures the window gets to measure it again: the terminal
      // behind the panel, and settle() below, which may now have to pull the
      // panel back towards the corner it is docked to.
      window.dispatchEvent(new Event('resize'));
    },
    [room],
  );

  const resetSize = useCallback(() => {
    // Removed from the element as well as from the state. A drag writes to the
    // DOM behind React's back, so React's idea of the old value is whatever it
    // last rendered, and a diff that sees no change would leave the dragged
    // size sitting on the element for ever.
    const panel = panelRef.current;
    panel?.style.removeProperty('--chat-w');
    panel?.style.removeProperty('--chat-h');
    setSize(null);
    updatePrefs({ size: null });
    window.dispatchEvent(new Event('resize'));
  }, []);

  /** Which edges one handle moves. The corner moves both. */
  type Grip = { w: boolean; h: boolean };

  const pull = (grip: Grip) => (e: ReactPointerEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel || e.button !== 0) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    handle.classList.add('is-dragging');

    const box = panel.getBoundingClientRect();
    const from = { x: e.clientX, y: e.clientY, w: box.width, h: box.height };
    // Measured once, at the start: the anchored corner does not move during a
    // resize, so neither does the room the panel has to grow into.
    const limit: Size = { w: box.right - DOCK_EDGE, h: box.bottom - DOCK_EDGE };
    const cursor = document.body.style.cursor;
    document.body.style.cursor = grip.w && grip.h ? 'nwse-resize' : grip.w ? 'ew-resize' : 'ns-resize';
    document.body.style.userSelect = 'none';

    const move = (ev: PointerEvent) => {
      const next = clampSize(
        {
          // The width handle is on the panel's inline-start edge, which is
          // its left in English and its right in Arabic — so pulling away
          // from the panel grows it, and which way that is depends on the
          // direction. `clientX` is physical and does not know.
          w: grip.w ? from.w + (rtl ? ev.clientX - from.x : from.x - ev.clientX) : from.w,
          h: grip.h ? from.h - (ev.clientY - from.y) : from.h,
        },
        limit,
      );
      panel.style.setProperty('--chat-w', `${next.w}px`);
      panel.style.setProperty('--chat-h', `${next.h}px`);
    };

    const done = () => {
      handle.classList.remove('is-dragging');
      document.body.style.cursor = cursor;
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', done);
      window.removeEventListener('pointercancel', done);
      // Once, at the end. A resize is one decision, not sixty writes.
      resizeTo(measure());
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', done);
    window.addEventListener('pointercancel', done);
  };

  const step = (grip: Grip) => (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Home') {
      e.preventDefault();
      resetSize();
      return;
    }
    // Away from the anchored corner is bigger, on the keyboard as well as
    // under the pointer.
    // Same mirroring as the pointer: the arrow that points away from the
    // panel is the one that grows it, and in Arabic that is the other arrow.
    const bigger = grip.w ? (rtl ? 'ArrowRight' : 'ArrowLeft') : 'ArrowUp';
    const smaller = grip.w ? (rtl ? 'ArrowLeft' : 'ArrowRight') : 'ArrowDown';
    if (e.key !== bigger && e.key !== smaller) return;
    e.preventDefault();
    const by = e.key === bigger ? RESIZE_STEP : -RESIZE_STEP;
    const now = measure();
    resizeTo({ w: grip.w ? now.w + by : now.w, h: grip.h ? now.h + by : now.h });
  };

  // A window that changed size, or a remembered position that no longer fits
  // the one it opens in. Runs when the panel appears, because a panel that is
  // not on screen has no size to measure.
  useEffect(() => {
    if (!open) return;
    const settle = () => {
      const current = dockRef.current;
      if (!current || drag.current) return;
      const next = fit(current);
      if (next.end === current.end && next.bottom === current.bottom) return;
      setDock(next);
      updatePrefs({ dock: next });
    };
    settle();
    window.addEventListener('resize', settle);
    return () => window.removeEventListener('resize', settle);
  }, [open, fit]);

  /**
   * Two kinds of news, and the bubble carries both.
   *
   * `unread` is the room on screen — messages that arrived while the panel was
   * shut or the tab was hidden — and it dies with the tab. `mentions` is what
   * the account is holding: lines that named you, in any room, kept by the
   * Worker until they are acknowledged, and still there tomorrow on another
   * machine. They are added for the badge's number and kept apart everywhere
   * else, because "someone said something" and "someone said something to
   * *you*" are not the same news and a learner deciding whether to open the
   * panel is deciding between exactly those two things.
   */
  const unread = chat.active.unread;
  const mentions = chat.inbox.mentions.length;
  const waiting = unread + mentions;

  /**
   * Chat is the one thing in the corner that changes while nobody is looking
   * at it, so it is the one thing the fold has to be able to report. The
   * count itself stays here — the bubble says it properly, and the fold is
   * twenty pixels wide — and what crosses over is only that there is one.
   */
  useEffect(() => {
    setCornerSignal(waiting > 0);
  }, [waiting]);
  // One function builds both the name and the tooltip, so the two cannot
  // drift — and it takes the facts rather than a finished label, because
  // "5 unread" is not one word away from "رسائل غير مقروءة" in either
  // direction: Arabic counts with six plural forms and an obligatory dual.
  const label = s.chat.dock.toggle(open, unread, mentions);

  // The size is always written, whether or not the learner has touched it, so
  // that the handles can say what their value is and the stylesheet's own
  // fallback is never the thing on screen.
  const shown = size ?? CHAT_DEFAULT;
  const style: CSSProperties = {};
  style['--chat-w' as never] = `${shown.w}px` as never;
  style['--chat-h' as never] = `${shown.h}px` as never;
  if (dock) {
    // `insetInlineEnd`, so the panel stays beside the bubble when the page
    // turns around. chat.css sets the same property, and this inline style is
    // what wins once the panel has been dragged.
    style.insetInlineEnd = dock.end;
    style.bottom = dock.bottom;
  }

  return (
    <>
      <button
        ref={toggleRef}
        type="button"
        className={
          ['chat-toggle', waiting > 0 ? 'has-unread' : '', mentions > 0 ? 'has-mention' : ''].filter(Boolean).join(' ')
        }
        aria-expanded={open}
        aria-controls="chat-drawer"
        aria-label={label}
        title={s.chat.dock.toggleWhy(open, unread, mentions)}
        onClick={() => show(!open)}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" width="20" height="20">
          <path
            d="M3 3.5h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H7.5L4.5 14v-2.5H3a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.35"
            strokeLinejoin="round"
          />
        </svg>
        {waiting > 0 && (
          /* The number is the whole of the news for an eye; which half of it
             is a mention is carried by the mark rather than by a second
             badge, because two numbers in a 44px bubble is neither. The
             accessible name above says both in words. */
          <span className={mentions > 0 ? 'chat-unread is-mention' : 'chat-unread'} aria-hidden="true">
            {waiting > 99 ? '99+' : waiting}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.aside
            ref={panelRef}
            key="chat"
            id="chat-drawer"
            className={over > 0 ? 'chat is-dropping' : 'chat'}
            role="dialog"
            aria-label={s.chat.dock.panel}
            /**
             * The whole panel is the drop target, not the composer.
             *
             * Somebody dragging a screenshot into a conversation aims at the
             * conversation — that is where the thing they are answering is —
             * and a target that is only the thirty-four pixel field at the
             * bottom is a target most people miss. There is no second
             * behaviour to disambiguate from: nothing else in this panel
             * accepts a drop, and the panel's own drag is pointer events on
             * the header, which is a different event stream entirely and
             * never fires for a file from the desktop.
             *
             * `preventDefault` on `dragover` is what makes the drop possible
             * at all; App.tsx cancels the same events at the window to stop
             * the browser navigating away, and that listener runs after this
             * one and does not care that it has already been cancelled.
             */
            onDragEnter={(e) => {
              if (!account.pictures || !dragHasFile(e.dataTransfer)) return;
              e.preventDefault();
              setOver((n) => n + 1);
            }}
            onDragOver={(e) => {
              // Not a target at all while pictures are off: without this the
              // panel would light up, accept the drop and then refuse it,
              // which is a worse answer than the browser's own "no".
              if (!account.pictures || !dragHasFile(e.dataTransfer)) return;
              e.preventDefault();
              // Says "this will be a copy" rather than "this will be a move",
              // which is what the cursor shows the person mid-drag.
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDragLeave={() => setOver((n) => Math.max(0, n - 1))}
            onDrop={(e) => {
              const file = account.pictures ? imageFrom(e.dataTransfer) : null;
              // Cancelled whether or not it was a picture: a drop the panel
              // does not want is still a drop the browser must not navigate
              // to, and it landed here rather than on the page.
              e.preventDefault();
              setOver(0);
              if (file) attach.pick(file);
            }}
            // The stylesheet's corner and the stylesheet's size until the
            // learner moves or pulls it.
            style={style}
            // Out of the bubble it belongs to, rather than in from the edge:
            // the panel is a card above the toggle now, not a drawer.
            initial={{ y: 10, scale: 0.97, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 10, scale: 0.97, opacity: 0 }}
            transition={ease}
            onKeyDown={onKeyDown}
          >
            <ChatPanel
              chat={chat}
              account={account}
              mission={mission}
              draft={draft}
              onDraft={setDraft}
              attach={attach}
              onClose={close}
              notify={notify}
              grip={grip}
              showSupport={supportAsk}
            />

            <div
              className="chat-rsz chat-rsz-w"
              role="separator"
              aria-orientation="vertical"
              aria-label={s.chat.dock.width}
              aria-valuemin={CHAT_MIN.w}
              aria-valuemax={CHAT_MAX.w}
              aria-valuenow={shown.w}
              tabIndex={0}
              title={s.chat.dock.resizeReset}
              onPointerDown={pull({ w: true, h: false })}
              onKeyDown={step({ w: true, h: false })}
              onDoubleClick={resetSize}
            />
            <div
              className="chat-rsz chat-rsz-h"
              role="separator"
              aria-orientation="horizontal"
              aria-label={s.chat.dock.height}
              aria-valuemin={CHAT_MIN.h}
              aria-valuemax={CHAT_MAX.h}
              aria-valuenow={shown.h}
              tabIndex={0}
              title={s.chat.dock.resizeReset}
              onPointerDown={pull({ w: false, h: true })}
              onKeyDown={step({ w: false, h: true })}
              onDoubleClick={resetSize}
            />
            {/* Both at once. Pointer only: the two edges above are the whole
                of it for a keyboard, and a third tab stop that does nothing
                new is a third tab stop in the way. */}
            <div
              className="chat-rsz chat-rsz-c"
              aria-hidden="true"
              title={s.chat.dock.resize}
              onPointerDown={pull({ w: true, h: true })}
              onDoubleClick={resetSize}
            />
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

export default ChatDock;
